import { css } from '@emotion/css';
import { Button, ButtonProps, Modal, styled } from '@mui/material';
import { AuthContext } from 'index';
import { memo, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOpenedCloseSurveyModal } from 'store/questionnaireSlice';
import { RootState } from 'store/store';
import { colors } from 'styles/colors';

const ColorButtonCancel = styled(Button)<ButtonProps>(() => ({
    color: '#000',
    backgroundColor: 'white',
    width: '6rem',
    height: '2.4rem',
    fontSize: '14px',
    fontWeight: '400',
    fontFamily: 'Figtree',
    padding: '5px',
    textTransform: 'none',
    '&:hover': {
        backgroundColor: 'white',
    },
    borderRadius: '0.5rem',
}));

const ColorButtonConfirm = styled(Button)<ButtonProps>(() => ({
    color: 'white',
    backgroundColor: colors.primaryButtonBackground,
    width: '6rem',
    height: '2.4rem',
    fontSize: '14px',
    fontWeight: '400',
    fontFamily: 'Figtree',
    padding: '5px',
    textTransform: 'none',
    '&:hover': {
        backgroundColor: colors.primaryButtonBackground,
    },
    borderRadius: '0.5rem',
}));

type InviteModallProps = {
    invite: () => void;
};

const InviteModal = memo(({ invite }: InviteModallProps) => {
    const dispatch = useDispatch();
    const openedInviteModal = useSelector((state: RootState) => state.global.openedInviteModal);
    const auth = useContext(AuthContext);

    const onLogout = useCallback(() => {
        dispatch(setOpenedCloseSurveyModal(false));
        localStorage.removeItem('id_token');
        if (process.env.NODE_ENV !== 'development') {
            localStorage.setItem('logoutCheck', Date.now().toString());
        }
        auth.logout({
            redirectUri: process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://planetdiet.dk/',
        });
    }, [auth, dispatch]);

    return (
        <Modal
            sx={{ border: 'none', outline: 'none' }}
            open={openedInviteModal}
            onClose={onLogout}
            style={{ backgroundColor: 'none' }}
        >
            <div className={styles.infoModal}>
                <div className={styles.modalContent}>
                    <div className={styles.modalBody}>
                        {
                            'Tak fordi du ønsker at deltage. For at kunne tilmelde dig projektet, har vi brug for at registrere dig i vores system. Du bliver registreret ved at trykke på knappen “Inviter” hvorefter vi vil sende dig en invitation via Digital Post.'
                        }
                    </div>
                    <div className={styles.modalFooter}>
                        <div className={styles.button}>
                            <ColorButtonCancel variant="contained" onClick={onLogout}>
                                Fortryd
                            </ColorButtonCancel>
                        </div>
                        <div className={styles.button}>
                            <ColorButtonConfirm variant="contained" onClick={invite}>
                                Inviter
                            </ColorButtonConfirm>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
});

const styles = {
    infoModal: css({
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '300px',
        backgroundColor: colors.backgroundLight,
        borderRadius: '10px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        padding: '1.5rem',
        outline: 'none',
    }),
    modalContent: css({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    }),
    modalBody: css({
        flex: 1,
        marginBottom: '1.5rem',
        fontSize: '1rem',
        color: colors.primaryText,
        textAlign: 'left',
        fontFamily: 'Figtree',
    }),
    modalFooter: css({
        display: 'flex',
        justifyContent: 'space-between',
    }),
    button: css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Figtree',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
    }),
};

export default InviteModal;
